import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Drawer,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Today as TodayIcon,
  CalendarToday as CalendarTodayIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { api } from '@services/authService';
import FullCalendarWeekView from '@shared/FullCalendarWeekView';
import { formatForDisplay } from '@utils/dateFormatter';

// Debug flag for development
const DEBUG = process.env.NODE_ENV === 'development';

// Types
interface Appointment {
  id: string;
  date?: string;
  time?: string;
  scheduledAt?: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'present' | 'rescheduled' | 'cancelled' | 'completed' | 'no_show';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  patientName?: string;
  clientName?: string;
  serviceName?: string;
  animalSpecies?: string;
  veterinarianName?: string;
  veterinarianId?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Additional fields that might exist in API response
  patient?: {
    name?: string;
    firstName?: string;
    lastName?: string;
    species?: string;
  };
  owner?: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
  doctor?: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
  };
  veterinarian?: {
    name?: string;
    firstName?: string;
    lastName?: string;
  };
  service?: {
    name?: string;
  };
  reason?: string;
  doctorId?: string;
  animalName?: string;
  ownerFirstName?: string;
  ownerLastName?: string;
  veterinarianFirstName?: string;
  veterinarianLastName?: string;
}

interface Veterinarian {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'pending';
  specializations: string[];
}

interface Filters {
  veterinarian: string;
  species: string;
  service: string;
  status: string | string[];
  search: string;
}

const ClinicAppointmentsCalendar: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [veterinarians, setVeterinarians] = useState<Veterinarian[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<'day' | 'week' | 'list'>('week');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [savedPresets, setSavedPresets] = useState<Array<{ name: string; filters: Filters }>>([]);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetMenuAnchor, setPresetMenuAnchor] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout | null>(null);
  
  // Filters state
  const [filters, setFilters] = useState<Filters>({
    veterinarian: 'all',
    species: 'all',
    service: 'all',
    status: 'all',
    search: ''
  });

  // Load veterinarians for clinic
  const loadVeterinarians = async () => {
    try {
      const response = await api.get('/veterinarians', {
        params: {
          clinicId: user?.clinicId,
          status: 'active',
          limit: 100
        }
      });
      if (DEBUG) console.log('🔍 Veterinarians response:', response.data);
      const veterinariansData = response.data?.data || response.data || [];
      setVeterinarians(Array.isArray(veterinariansData) ? veterinariansData : []);
    } catch (err: any) {
      console.error('Eroare la încărcarea medicilor:', err);
      setVeterinarians([]);
    }
  };

  // Load appointments for all veterinarians in clinic
  const loadAppointments = async () => {
    const controller = new AbortController();
    
    try {
      setLoading(true);
      setError('');
      
      const params: any = {
        from: selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0'),
        to: new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000).getFullYear() + '-' + String(new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000).getMonth() + 1).padStart(2, '0') + '-' + String(new Date(selectedDate.getTime() + 7 * 24 * 60 * 60 * 1000).getDate()).padStart(2, '0'),
        clinicId: user?.clinicId
      };

      // Add filters
      if (filters.veterinarian !== 'all') {
        params.veterinarianId = filters.veterinarian;
      }
      if (filters.species !== 'all') {
        params.species = filters.species;
      }
      if (filters.service !== 'all') {
        params.service = filters.service;
      }
      if (filters.status !== 'all') {
        params.status = Array.isArray(filters.status) ? filters.status : [filters.status];
      }
      if (filters.search) {
        params.search = filters.search;
      }

      const response = await api.get('/appointments', { 
        params,
        signal: controller.signal 
      });
      if (DEBUG) console.log('🔍 Appointments response:', response.data);
      const appointmentsData = response.data?.data || response.data || [];
      if (DEBUG) {
        console.log('🔍 First appointment:', appointmentsData[0]);
        console.log('🔍 All appointments:', appointmentsData);
        
        // Log detailed structure of first appointment
        if (appointmentsData[0]) {
          console.log('🔍 First appointment keys:', Object.keys(appointmentsData[0]));
          console.log('🔍 First appointment values:', Object.values(appointmentsData[0]));
        }
      }
      if (!controller.signal.aborted) {
        setAppointments(Array.isArray(appointmentsData) ? appointmentsData : []);
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        console.error('Eroare la încărcarea programărilor:', err);
        setError('Eroare la încărcarea programărilor');
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
    
    return () => controller.abort();
  };

  // Load saved presets from localStorage
  useEffect(() => {
    const savedPresetsStr = localStorage.getItem('clinicAppointmentPresets');
    if (savedPresetsStr) {
      try {
        setSavedPresets(JSON.parse(savedPresetsStr));
      } catch (e) {
        console.error('Error loading presets:', e);
      }
    }
  }, []);

  // URL sync for filters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlFilters: Partial<Filters> = {};
    
    if (params.get('medic')) urlFilters.veterinarian = params.get('medic')!;
    if (params.get('specie')) urlFilters.species = params.get('specie')!;
    if (params.get('serviciu')) urlFilters.service = params.get('serviciu')!;
    if (params.get('status')) {
      const statusParam = params.get('status')!;
      urlFilters.status = statusParam.includes(',') ? statusParam.split(',') : statusParam;
    }
    if (params.get('cauta')) urlFilters.search = params.get('cauta')!;
    
    if (Object.keys(urlFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...urlFilters }));
    }
  }, []);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.veterinarian !== 'all') params.set('medic', filters.veterinarian);
    if (filters.species !== 'all') params.set('specie', filters.species);
    if (filters.service !== 'all') params.set('serviciu', filters.service);
    if (filters.status !== 'all') {
      const statusValue = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
      params.set('status', statusValue);
    }
    if (filters.search) params.set('cauta', filters.search);
    
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [filters]);

  // Load data on component mount and when filters change
  useEffect(() => {
    loadVeterinarians();
  }, []);

  useEffect(() => {
    loadAppointments();
  }, [selectedDate, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType: keyof Filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    
    // Debounce search
    if (filterType === 'search') {
      if (searchDebounce) {
        clearTimeout(searchDebounce);
      }
      const timeout = setTimeout(() => {
        // Search is handled by useEffect
      }, 300);
      setSearchDebounce(timeout);
    }
    
    // Show feedback for filter changes
    if (filterType === 'status' && value !== 'all') {
      setSnackbar({
        open: true,
        message: 'Filtre aplicate cu succes',
        severity: 'success'
      });
    }
  };

  // Handle date change
  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate);
  };

  // Handle add appointment
  const handleAddAppointment = () => {
    if (DEBUG) console.log('New appointment clicked');
    // TODO: Implement add appointment logic
  };

  // Handle today button
  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Handle save preset
  const handleSavePreset = () => {
    if (!presetName.trim()) return;
    
    const newPreset = { name: presetName.trim(), filters: { ...filters } };
    const updatedPresets = [...savedPresets, newPreset];
    setSavedPresets(updatedPresets);
    localStorage.setItem('clinicAppointmentPresets', JSON.stringify(updatedPresets));
    setPresetName('');
    setPresetDialogOpen(false);
  };

  // Handle load preset
  const handleLoadPreset = (preset: { name: string; filters: Filters }) => {
    setFilters(preset.filters);
    setFiltersDrawerOpen(false);
  };

  // Handle delete preset
  const handleDeletePreset = (index: number) => {
    const updatedPresets = savedPresets.filter((_, i) => i !== index);
    setSavedPresets(updatedPresets);
    localStorage.setItem('clinicAppointmentPresets', JSON.stringify(updatedPresets));
  };

  // Get unique species from appointments
  const uniqueSpecies = useMemo(() => {
    const species = (appointments || []).map(apt => apt.animalSpecies).filter(Boolean);
    return [...new Set(species)].sort();
  }, [appointments]);

  // Get unique services from appointments
  const uniqueServices = useMemo(() => {
    const services = (appointments || []).map(apt => apt.serviceName).filter(Boolean);
    return [...new Set(services)].sort();
  }, [appointments]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.veterinarian !== 'all') count++;
    if (filters.species !== 'all') count++;
    if (filters.service !== 'all') count++;
    if (filters.status !== 'all') count++;
    if (filters.search) count++;
    return count;
  }, [filters]);

  // Status options - set canonic conform brief
  const statusOptions = [
    { value: 'scheduled', label: 'Neconfirmat', color: '#9e9e9e' }, // gri
    { value: 'confirmed', label: 'Confirmat', color: '#4caf50' }, // verde
    { value: 'rescheduled', label: 'Reprogramat', color: '#ff9800' }, // portocaliu
    { value: 'present', label: 'Check-in', color: '#2196f3' }, // albastru
    { value: 'completed', label: 'Finalizat', color: '#9c27b0' }, // mov
    { value: 'cancelled', label: 'Anulat', color: '#e74c3c' }, // roșu mai închis
    { value: 'no_show', label: 'No-show', color: '#ffb6b6' } // roz deschis
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Programări Clinică
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Vizualizare și gestionare programări pentru toți medicii din clinică
        </Typography>
      </Box>

      {/* Sticky Filters Bar - Desktop / Mobile Button */}
      <Paper 
        id="filters-bar"
        sx={{ 
          p: 2, 
          mb: 1.5, 
          borderRadius: 2,
          position: 'sticky',
          top: theme.spacing(8),
          zIndex: theme.zIndex.appBar + 1,
          backgroundColor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        {isMobile ? (
          /* Mobile: Show Filters Button */
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setFiltersDrawerOpen(true)}
            fullWidth
          >
            Filtre {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </Button>
        ) : (
          /* Desktop: Show Filters */
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          {/* Search */}
          <TextField
            size="small"
            placeholder="Caută programări..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Veterinarian Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Medic</InputLabel>
            <Select
              value={filters.veterinarian}
              onChange={(e) => handleFilterChange('veterinarian', e.target.value)}
              label="Medic"
            >
              <MenuItem value="all">Toți medicii</MenuItem>
              {(veterinarians || []).map((vet) => (
                <MenuItem key={vet.id} value={vet.id}>
                  {vet.firstName} {vet.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Species Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Specie</InputLabel>
            <Select
              value={filters.species}
              onChange={(e) => handleFilterChange('species', e.target.value)}
              label="Specie"
            >
              <MenuItem value="all">Toate speciile</MenuItem>
              {uniqueSpecies.map((species) => (
                <MenuItem key={species} value={species}>
                  {species}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service Filter */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Serviciu</InputLabel>
            <Select
              value={filters.service}
              onChange={(e) => handleFilterChange('service', e.target.value)}
              label="Serviciu"
            >
              <MenuItem value="all">Toate serviciile</MenuItem>
              {uniqueServices.map((service) => (
                <MenuItem key={service} value={service}>
                  {service}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
              Status:
            </Typography>
            {statusOptions.map((status) => (
              <Chip
                key={status.value}
                label={status.label}
                size="small"
                onClick={() => {
                  const currentStatus = Array.isArray(filters.status) ? filters.status : [filters.status];
                  const newStatus = currentStatus.includes(status.value)
                    ? currentStatus.filter(s => s !== status.value)
                    : [...currentStatus, status.value];
                  handleFilterChange('status', newStatus.length === 0 ? 'all' : newStatus);
                }}
                variant={
                  (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                    ? 'filled'
                    : 'outlined'
                }
                sx={{
                  backgroundColor: 
                    (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                      ? status.color
                      : 'transparent',
                  color: 
                    (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                      ? 'white'
                      : status.color,
                  borderColor: status.color,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              />
            ))}
          </Box>

          {/* Reset Button */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setFilters({
                veterinarian: 'all',
                species: 'all',
                service: 'all',
                status: 'all',
                search: ''
              });
            }}
          >
            Reset filtre
          </Button>
          
          {/* Save Button */}
          <Button
            size="small"
            variant="contained"
            startIcon={<BookmarkIcon />}
            onClick={() => setPresetDialogOpen(true)}
            title="Salvează acest set de filtre ca preset numit"
            sx={{ gap: 0.75 }}
          >
            Salvează filtrele
          </Button>

          {/* Load Presets Button */}
          {savedPresets.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<BookmarkBorderIcon />}
              onClick={(e) => setPresetMenuAnchor(e.currentTarget)}
              title="Încarcă un preset salvat"
              sx={{ gap: 0.75 }}
            >
              Presets ({savedPresets.length})
            </Button>
          )}
        </Stack>
        )}
      </Paper>

      {/* Mobile Filters Drawer */}
      <Drawer
        anchor="left"
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtre
          </Typography>
          
          <Stack spacing={2}>
            {/* Search */}
            <TextField
              size="small"
              placeholder="Caută programări..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Medic Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Medic</InputLabel>
              <Select
                value={filters.veterinarian}
                onChange={(e) => handleFilterChange('veterinarian', e.target.value)}
                label="Medic"
              >
                <MenuItem value="all">Toți medicii</MenuItem>
                {(veterinarians || []).map((vet) => (
                  <MenuItem key={vet.id} value={vet.id}>
                    {vet.firstName} {vet.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Specie Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Specie</InputLabel>
              <Select
                value={filters.species}
                onChange={(e) => handleFilterChange('species', e.target.value)}
                label="Specie"
              >
                <MenuItem value="all">Toate speciile</MenuItem>
                {uniqueSpecies.map((species) => (
                  <MenuItem key={species} value={species}>
                    {species}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Serviciu Filter */}
            <FormControl size="small" fullWidth>
              <InputLabel>Serviciu</InputLabel>
              <Select
                value={filters.service}
                onChange={(e) => handleFilterChange('service', e.target.value)}
                label="Serviciu"
              >
                <MenuItem value="all">Toate serviciile</MenuItem>
                {uniqueServices.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {/* Status Chips */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#555', mb: 1 }}>
                Status:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {statusOptions.map((status) => (
                  <Chip
                    key={status.value}
                    label={status.label}
                    size="small"
                    onClick={() => {
                      const currentStatus = Array.isArray(filters.status) ? filters.status : [filters.status];
                      const newStatus = currentStatus.includes(status.value)
                        ? currentStatus.filter(s => s !== status.value)
                        : [...currentStatus, status.value];
                      handleFilterChange('status', newStatus.length === 0 ? 'all' : newStatus);
                    }}
                    variant={
                      (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                        ? 'filled'
                        : 'outlined'
                    }
                    sx={{
                      backgroundColor: 
                        (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                          ? status.color
                          : 'transparent',
                      color: 
                        (Array.isArray(filters.status) ? filters.status : [filters.status]).includes(status.value)
                          ? 'white'
                          : status.color,
                      borderColor: status.color,
                      '&:hover': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setFilters({
                    veterinarian: 'all',
                    species: 'all',
                    service: 'all',
                    status: 'all',
                    search: ''
                  });
                }}
                fullWidth
              >
                Reset filtre
              </Button>
              
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => {
                  localStorage.setItem('clinicAppointmentLast', JSON.stringify(filters));
                  setFiltersDrawerOpen(false);
                }}
                fullWidth
              >
                Salvează
              </Button>
            </Stack>

            <Button
              variant="text"
              onClick={() => setFiltersDrawerOpen(false)}
              fullWidth
            >
              Închide
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Navigation & CTA Bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          {/* Time Navigation */}
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Previous/Next Buttons */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() - 7);
                setSelectedDate(newDate);
              }}
            >
              ‹
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const newDate = new Date(selectedDate);
                newDate.setDate(newDate.getDate() + 7);
                setSelectedDate(newDate);
              }}
            >
              ›
            </Button>

            {/* Today Button */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={handleToday}
            >
              Azi
            </Button>

            {/* View Segmented Control */}
            <Stack direction="row" spacing={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
              {[
                { value: 'day', label: 'Zi' },
                { value: 'week', label: 'Săptămână' },
                { value: 'list', label: 'Listă' }
              ].map((viewOption) => (
                <Button
                  key={viewOption.value}
                  size="small"
                  variant={viewOption.value === view ? 'contained' : 'text'}
                  onClick={() => setView(viewOption.value as 'day' | 'week' | 'list')}
                  sx={{
                    minWidth: 80,
                    borderRadius: 0,
                    '&:first-of-type': { borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
                    '&:last-of-type': { borderTopRightRadius: 4, borderBottomRightRadius: 4 }
                  }}
                >
                  {viewOption.label}
                </Button>
              ))}
            </Stack>

            {/* Date Picker */}
            <TextField
              size="small"
              type="date"
              value={selectedDate.getFullYear() + '-' + String(selectedDate.getMonth() + 1).padStart(2, '0') + '-' + String(selectedDate.getDate()).padStart(2, '0')}
              onChange={(e) => setSelectedDate(new Date(e.target.value + 'T00:00'))}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
          </Stack>

          {/* CTA Button */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              // Handle new appointment
              if (DEBUG) console.log('New appointment clicked');
            }}
            sx={{ minWidth: 160 }}
          >
            Programare nouă
          </Button>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && appointments.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nu există programări pentru filtrele selectate
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Încearcă să modifici filtrele sau să adaugi o programare nouă
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  veterinarian: 'all',
                  species: 'all',
                  service: 'all',
                  status: 'all',
                  search: ''
                });
              }}
            >
              Șterge filtrele
            </Button>
            <Button
              variant="contained"
              onClick={handleAddAppointment}
            >
              Programare nouă
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Calendar */}
      {appointments.length > 0 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <FullCalendarWeekView
          appointments={appointments.map(apt => {
            try {
              if (DEBUG) {
                console.log('🔍 Processing appointment:', apt);
                console.log('🔍 apt.patientName:', apt.patientName);
                console.log('🔍 apt.veterinarianName:', apt.veterinarianName);
                console.log('🔍 apt.serviceName:', apt.serviceName);
                console.log('🔍 apt.patient:', apt.patient);
                console.log('🔍 apt.veterinarian:', apt.veterinarian);
                console.log('🔍 apt.service:', apt.service);
                console.log('🔍 apt.owner:', apt.owner);
                console.log('🔍 apt.doctor:', apt.doctor);
                console.log('🔍 apt.reason:', apt.reason);
                console.log('🔍 apt.doctorId:', apt.doctorId);
              }
              
              // Handle different date formats - check for scheduledAt first, then date+time
              let startDate: Date;
              if (apt.scheduledAt) {
                startDate = new Date(apt.scheduledAt);
              } else if (apt.date && apt.time) {
                startDate = new Date(apt.date + 'T' + apt.time);
              } else {
                console.error('No valid date found for appointment:', apt);
                throw new Error('No valid date');
              }
              
              const endDate = new Date(startDate.getTime() + (apt.duration || 30) * 60000);
              
              // Verifică dacă datele sunt valide
              if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                console.error('Invalid date for appointment:', apt);
                throw new Error('Invalid date');
              }
              
              return {
                id: apt.id,
                clinicId: user?.clinicId || '',
                doctorId: apt.veterinarianId,
                start: startDate,
                end: endDate,
                notes: apt.notes,
                status: apt.status === 'present' ? 'present' : 
                        apt.status === 'rescheduled' ? 'rescheduled' : 
                        apt.status === 'cancelled' ? 'cancelled' :
                        apt.status === 'completed' ? 'completed' :
                        apt.status === 'confirmed' ? 'confirmed' :
                        'scheduled',
                createdAt: apt.createdAt,
                updatedAt: apt.updatedAt,
                // Additional fields for compatibility
                date: apt.date || startDate.toISOString().split('T')[0],
                time: apt.time || startDate.toTimeString().split(' ')[0].substring(0, 5),
                patientName: apt.animalName || apt.patient?.name || apt.patientName || 'Pacient necunoscut',
                clientName: `${apt.ownerFirstName || ''} ${apt.ownerLastName || ''}`.trim() || apt.clientName || 'Client necunoscut',
                veterinarianName: `Dr. ${apt.veterinarianFirstName || ''} ${apt.veterinarianLastName || ''}`.trim() || apt.doctor?.fullName || apt.veterinarianName || 'Medic necunoscut',
                veterinarianId: apt.veterinarianId || apt.doctorId,
                serviceName: apt.serviceName || apt.reason || 'Serviciu necunoscut',
                priority: apt.priority === 'normal' ? 'medium' : apt.priority,
                animalSpecies: apt.animalSpecies,
                duration: apt.duration
              };
            } catch (error) {
              console.error('Error processing appointment:', apt, error);
              return null as any;
            }
          }).filter((apt): apt is NonNullable<typeof apt> => apt !== null)}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          onAppointmentClick={() => {}}
          onAddAppointment={() => {}}
          veterinarians={(veterinarians || []).map(vet => ({
            id: vet.id,
            title: `${vet.firstName} ${vet.lastName}`,
            color: '#1976d2'
          }))}
        />
        </Paper>
      )}

      {/* Preset Dialog */}
      <Dialog open={presetDialogOpen} onClose={() => setPresetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Salvează preset filtre</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nume preset"
            type="text"
            fullWidth
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            placeholder="Ex: Programări astăzi, Neconfirmate săptămâna..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSavePreset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialogOpen(false)}>Anulează</Button>
          <Button onClick={handleSavePreset} variant="contained" disabled={!presetName.trim()}>
            Salvează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Presets Menu */}
      <Menu
        anchorEl={presetMenuAnchor}
        open={Boolean(presetMenuAnchor)}
        onClose={() => setPresetMenuAnchor(null)}
      >
        {savedPresets.map((preset, index) => (
          <MenuItem key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 250 }}>
            <Box onClick={() => {
              handleLoadPreset(preset);
              setPresetMenuAnchor(null);
            }} sx={{ flex: 1, cursor: 'pointer' }}>
              <ListItemIcon>
                <BookmarkIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={preset.name} />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePreset(index);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClinicAppointmentsCalendar;
